import { Link } from 'react-router-dom';
import {
  Mail,
  Boxes,
  ClipboardList,
  FileText,
  FolderTree,
  Globe,
  Layers,
  Package,
  PackagePlus,
  Tag,
  Truck,
  X,
  type LucideIcon,
} from 'lucide-react';
import type { TaskContext, TaskContextType, TaskMember } from '@shared/types/tasks';
import { TASK_CONTEXT_TYPE_LABELS } from '@shared/task-contexts';
import { memberName } from './task-chips';
import { MemberAvatar } from '@/components/team/MemberAvatar';
import { taskPerson } from './task-people';
import { cn } from '@/lib/utils';

// Label and member chips live in task-chips.tsx (no router or stores), so the Chrome extension can use them too.
export {
  LabelChip,
  memberHandle,
  memberName,
} from './task-chips';

export const CONTEXT_ICONS: Record<TaskContextType, LucideIcon> = {
  product_batch: PackagePlus,
  product_batch_product: Boxes,
  product: Package,
  variant: Layers,
  vendor: Tag,
  supplier: Truck,
  purchase_order: ClipboardList,
  collection: FolderTree,
  conversation: Mail,
  page: FileText,
  url: Globe,
};

export function AvatarStack({
  userIds,
  members,
  max = 3,
}: {
  userIds: string[];
  members: TaskMember[];
  max?: number;
}) {
  if (!userIds.length) return null;
  const shown = userIds.slice(0, max);
  return (
    <div className="flex -space-x-2">
      {shown.map((id) => {
        const person = taskPerson(members, id);
        return <MemberAvatar key={id} name={memberName(person)} photourl={person?.photourl} size="sm" ring />;
      })}
      {userIds.length > max ? (
        <span className="flex size-6 items-center justify-center rounded-full bg-muted text-3xs font-medium ring-2 ring-background">
          +{userIds.length - max}
        </span>
      ) : null}
    </div>
  );
}

/** A Context as a small chip; clicking goes to that page in jflo (a web page opens in a new tab). */
export function ContextChip({
  context,
  onRemove,
  onNavigate,
  compact = false,
}: {
  context: Pick<TaskContext, 'type' | 'title' | 'url'>;
  onRemove?: () => void;
  onNavigate?: () => void;
  compact?: boolean;
}) {
  const Icon = CONTEXT_ICONS[context.type];
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1 rounded-md border bg-muted/40 text-xs',
        compact ? 'px-1.5 py-0.5' : 'px-2 py-1'
      )}
    >
      <Icon className="size-3 shrink-0 text-muted-foreground" aria-hidden />
      {!context.url ? (
        <span className="min-w-0 truncate">{context.title}</span>
      ) : context.type === 'url' ? (
        <a
          href={context.url}
          target="_blank"
          rel="noreferrer noopener"
          onClick={(e) => e.stopPropagation()}
          className="min-w-0 truncate hover:underline"
          data-hint={`${TASK_CONTEXT_TYPE_LABELS.url}: ${context.url}`}
        >
          {context.title}
        </a>
      ) : (
      <Link
        to={context.url}
        onClick={(e) => {
          e.stopPropagation();
          onNavigate?.();
        }}
        className="min-w-0 truncate hover:underline"
        data-hint={`${TASK_CONTEXT_TYPE_LABELS[context.type]}: ${context.title}`}
      >
        {context.title}
      </Link>
      )}
      {onRemove ? (
        <button
          type="button"
          className="rounded text-muted-foreground hover:text-foreground"
          aria-label={`Remove ${context.title}`}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <X className="size-3" />
        </button>
      ) : null}
    </span>
  );
}

/** Moved to `@/lib/formatters` (step 1.8); kept for the task components' imports. */
export { formatRelative } from '@/lib/formatters';
